<template>
  <div v-if="isLoading">
    <!-- Skeleton loaders -->
    <div class="flex flex-col mb-4">
      <Skeleton class="w-full h-8 rounded-md mt-4" />
      <Skeleton class="w-full h-8 rounded-md mt-6" />
      <Skeleton class="w-full h-6 rounded-md mt-4" />
      <div v-for="n in 8" :key="n" class="flex justify-between gap-2 mt-6">
        <Skeleton class="w-36 h-6 rounded-md" />
        <Skeleton class="w-12 h-6 rounded-md" />
      </div>
    </div>
  </div>
  <div v-else class="m-0 h-full">
    <PaymentBreakdownHeader
      :show="showPaymentBreakdown"
      @back="back"
      @toggle="showPaymentBreakdown = !showPaymentBreakdown"
    />
    <PaymentBreakdownSchedule :show="showPaymentBreakdown" :titles="titles" :payments="convertedPayments" />
    <SectionDivider />
    <div class="flex items-center justify-between my-4">
      <div class="flex flex-col">
        <h4 class="text-grey-light-900 text-lg font-medium">Cancellation policy</h4>
        <p class="text-left text-sm text-brand-700">For a full cash refund, transaction fees will apply.</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import SectionDivider from '@/components/common/SectionDivider.vue';
import { Skeleton } from '@/components/ui/skeleton';
import PaymentBreakdownHeader from '@/components/widget/payments-breakdown/sections/PaymentBreakdownHeader.vue';
import PaymentBreakdownSchedule from '@/components/widget/payments-breakdown/sections/PaymentBreakdownSchedule.vue';
import { NetworkError } from '@/errors';
import type { PaymentSummary } from '@/models/payments';
import * as paymentPlanService from '@/services/paymentPlans';
import { useWidgetStore } from '@/stores/widget';
import { getEndDateMax, getEndDateMin } from '@/utils/boost.utils';
import { displayCurrency } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { pluralOrSingularText } from '@/utils/text.utils';
import dayjs from 'dayjs';
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import {
  ModelOrderRequestDepositTypeEnum,
  ModelPaymentPlanPreviewRequestFrequencyEnum,
  type ModelPaymentResponse,
} from '@layup/api-sdk';
import type { ScheduleBreakdownPayment } from './payments-breakdown.types';

const router = useRouter();
const convertedPayments = ref([] as ScheduleBreakdownPayment[]);
const firstPaymentAmount = ref(0);
const instalmentAmount = ref(0);
const showPaymentBreakdown = ref(true);
const widgetStore = useWidgetStore();
const quantity = ref(0);
const titles = ref<PaymentSummary[]>([]);
const isLoading = ref(true);
const errorMessage = ref('');

const rewardType = widgetStore.boost.merchant.voucherSettings?.rewardType;
const goalAmount = widgetStore.boost.newBoost.savingsGoal;
const rewardPercentage =
  rewardType === 'PERCENTAGE' ? widgetStore.boost.merchant.voucherSettings?.rewardPercentage : null;
const rewardAmount = getRewardAmount();

onMounted(async () => {
  try {
    const request = {
      amountDue: widgetStore.boost.newBoost.savingsGoal,
      endDateMin: getEndDateMin(),
      endDateMax: getEndDateMax(widgetStore.boost.newBoost.billingDay),
      frequency: ModelPaymentPlanPreviewRequestFrequencyEnum.Monthly,
      quantity: widgetStore.boost.newBoost.duration,
      depositType: ModelOrderRequestDepositTypeEnum.Flat,
      depositPerc: 0,
      offset: widgetStore.boost.newBoost.billingDay,
      depositAmount: widgetStore.boost.newBoost.deposit || 0,
    };
    const response = await paymentPlanService.createPaymentPlanPreview(request);
    if (response?.paymentPlans) {
      getPayments(response.paymentPlans[0].payments as ModelPaymentResponse[]);
    }
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('get-plan-preview', { status: 'network_error', channel: 'widget' });
      errorMessage.value = 'Network Error. Please try again.';
    } else {
      addMetric('get-plan-preview', { status: 'error', channel: 'widget' });
      createException(error);
      errorMessage.value = 'An unexpected error occurred. Please refresh to try again.';
    }
  } finally {
    isLoading.value = false;
  }
});

function getRewardAmount() {
  if (!goalAmount) return undefined;

  if (widgetStore.boost.merchant.voucherSettings?.rewardType === 'PERCENTAGE') {
    return (goalAmount * widgetStore.boost.merchant.voucherSettings?.rewardPercentage!) / 100;
  }
  return widgetStore.boost.merchant.voucherSettings?.rewardFlatAmountInCents;
}

const getPayments = (payments: ModelPaymentResponse[]) => {
  quantity.value = widgetStore.boost.newBoost.duration || 0;
  if (payments) {
    convertedPayments.value = payments
      .filter((p: ModelPaymentResponse, index: number) => index !== 0)
      .map((p: any) => {
        return {
          id: p._id!,
          amount: displayCurrency(p.amount),
          due: `${dayjs(p.due).format('MMMM')}`,
        };
      }) as ScheduleBreakdownPayment[];

    firstPaymentAmount.value = payments[0].amount || 0;
    instalmentAmount.value = payments[1].amount || 0;
  }

  // If the first payment is a deposit. It was explicitly set and the shopper is busy with a contribution.
  const isDeposit = !payments[0].due;
  if (isDeposit) {
    buildTitlesWithDeposit();
  } else {
    buildTitles();
  }
};

function back() {
  router.back();
}

function buildTitles() {
  titles.value = [
    { label: 'Total Goal Amount', value: displayCurrency(goalAmount), grey: false },
    {
      label: rewardPercentage ? `Reward amount (${rewardPercentage}%)` : 'Reward amount',
      value: displayCurrency(rewardAmount),
      grey: true,
    },
    { label: 'Monthly Instalments', value: displayCurrency(instalmentAmount.value), grey: false },
    { label: 'First Payment (Suggested)', value: displayCurrency(instalmentAmount.value), grey: true },
    {
      label: 'Duration',
      value: `${quantity.value} ${pluralOrSingularText(quantity.value, 'month')}`,
      grey: false,
    },
  ];
}

function buildTitlesWithDeposit() {
  titles.value = [
    { label: 'Total Goal Amount', value: displayCurrency(goalAmount), grey: false },
    {
      label: rewardPercentage ? `Reward amount (${rewardPercentage}%)` : 'Reward amount',
      value: displayCurrency(rewardAmount),
      grey: true,
    },
    { label: 'Monthly Instalments', value: displayCurrency(instalmentAmount.value), grey: false },
    { label: 'First Payment', value: displayCurrency(firstPaymentAmount.value), grey: false },
    {
      label: 'Duration',
      value: `${quantity.value} ${pluralOrSingularText(quantity.value, 'month')}`,
      grey: false,
    },
  ];
}
</script>
